import { useEffect } from "react";
import s from "./activation.module.css";
import { useActivation } from "./ActivationContext";
import { useTrainingMode } from "../training";
import { isAutoAdvanceOnClickSubstep } from "./activationPath";

/**
 * In-page step banner — replaced by ActivationTour floating callout.
 */
export default function ActivationPageGuide() {
  return null;
}

/**
 * Wraps a control; lifts when step/substep is active.
 */
export function ActTarget({
  stepId,
  subId = null,
  children,
  className = "",
  as: Comp = "div",
  compact = false,
  field = false,
  onActivate,
}) {
  const {
    visible,
    currentStep,
    currentSubstepId,
    completed,
    skipped,
    advanceSubstep,
  } = useActivation();
  const {
    active: trainingActive,
    currentLesson,
    completed: trainingCompleted,
    skipped: trainingSkipped,
    trainingSubstepId,
    advanceTrainingSubstep,
  } = useTrainingMode();

  const stepActive =
    visible &&
    currentStep?.id === stepId &&
    !completed.includes(stepId) &&
    !skipped.includes(stepId);

  const stepHasSubs = (currentStep?.substeps || []).length > 0;
  const activationHighlight = stepHasSubs
    ? stepActive && subId && currentSubstepId === subId
    : stepActive && !subId;

  const trainingHighlight =
    trainingActive &&
    currentLesson?.id === stepId &&
    !trainingCompleted.includes(stepId) &&
    !trainingSkipped.includes(stepId) &&
    subId &&
    trainingSubstepId === subId;

  const shouldHighlight = activationHighlight || trainingHighlight;
  const showFieldHighlight = shouldHighlight && field;
  const showControlHighlight = shouldHighlight && !field;

  useEffect(() => {
    if (!shouldHighlight) return undefined;
    const t = window.setTimeout(() => {
      const el = document.querySelector(
        `[data-act-target="${stepId}"][data-act-active="1"]`,
      );
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 180);
    return () => window.clearTimeout(t);
  }, [shouldHighlight, stepId]);

  const handleClickCapture = () => {
    if (!shouldHighlight) return;
    onActivate?.();
    if (!subId || !isAutoAdvanceOnClickSubstep(subId)) return;
    window.setTimeout(() => {
      if (trainingHighlight) advanceTrainingSubstep();
      else if (stepHasSubs) advanceSubstep();
    }, 280);
  };

  return (
    <Comp
      className={[
        className,
        showControlHighlight ? s.actTargetOn : "",
        showControlHighlight && compact ? s.actTargetBtn : "",
        showFieldHighlight ? s.actTargetField : "",
        showFieldHighlight ? s.actTargetOn : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-act-target={stepId}
      data-act-sub={subId || undefined}
      data-act-active={shouldHighlight ? "1" : undefined}
      onClickCapture={field ? undefined : handleClickCapture}
    >
      {children}
    </Comp>
  );
}
